import { Component, OnInit } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ResizeService } from 'src/app/shared/resize.service';
import { ConfirmPasswordValidator } from '../auth-helper/confirm-password.validator';
import { AuthService } from '../auth.service';
import { ToastControllerService } from 'src/app/pages/services/toast-controller.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
})
export class ResetPasswordComponent implements OnInit {
  msg!: string;
  resetForm!: FormGroup;
  uid: string | null;
  token: string | null;
  isLoading$: Observable<boolean> | undefined;
  error!: string;
  screenType!: string;
  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private toastcontrollerservice: ToastControllerService,
    private resizeService: ResizeService
  ) {
    this.isLoading$ = this.authService.isLoading$;
    this.uid = this.route.snapshot.paramMap.get('uid');
    this.token = this.route.snapshot.paramMap.get('token');
    this.resizeService.screenWidthChange$.subscribe({
      next: (res: string) => {
        this.screenType = res;
      },
    });
  }
  ngOnInit(): void {
    this.initForm();
  }
  initForm() {
    this.resetForm = this.fb.group(
      {
        new_password: [null, Validators.compose([Validators.required, Validators.maxLength(100)])],
        confirm_password: [
          null,
          Validators.compose([Validators.required, Validators.maxLength(100)]),
        ],
      },
      {
        validators: [
          ConfirmPasswordValidator.MatchPassword,
          ConfirmPasswordValidator.PasswordLength,
        ],
      } as AbstractControlOptions
    );
  }
  reset() {
    // this.loadingSubject.next(true)
    const resetModel: ResetModel = {
      uid: this.uid,
      token: this.token,
      new_password: this.resetForm.value['new_password'],
      confirm_password: this.resetForm.value['confirm_password'],
    };
    this.authService.resetPassword(resetModel).subscribe({
      next: () => {
        this.router.navigate(['/login']);
      },
      error: (err: { error: { message: string } }) => {
        this.handleError(err);
      },
    });
  }
  handleError(err: { error: { message: string } }) {
    if (err.error && err.error.message) {
      const formattedError = this.formatPasswordErrors(err.error.message);
      this.error = formattedError;
      this.toastcontrollerservice.failureToastVisible(formattedError);
    } else {
      this.error = err.error?.message || 'An error occurred';
      this.toastcontrollerservice.failureToastVisible(this.error);
    }
  }

  formatPasswordErrors(
    errorMessage:
      | string
      | {
          new_password: { code: string; string: string }[];
          confirm_password: { string: string }[];
        }
  ): string {
    if (typeof errorMessage === 'string') {
      const errorMessages: string[] = [];

      if (errorMessage.includes('password_too_common')) {
        errorMessages.push('Please choose a less common password.');
      }
      if (errorMessage.includes('password_entirely_numeric')) {
        errorMessages.push('Password cannot be entirely numeric.');
      }
      if (errorMessage.includes('password_too_short')) {
        errorMessages.push('Password must be at least 8 characters long.');
      }
      if (errorMessage.includes('password_too_similar')) {
        errorMessages.push('Password is too similar to your personal information.');
      }

      return errorMessages.length > 0
        ? errorMessages.join(' ')
        : 'Password validation failed. Please check your input.';
    }

    const errorMessages: string[] = [];

    if (errorMessage.new_password && Array.isArray(errorMessage.new_password)) {
      errorMessage.new_password.forEach((error: { code: string; string: string }) => {
        if (error.code === 'password_too_common') {
          errorMessages.push('Please choose a less common password.');
        } else if (error.code === 'password_entirely_numeric') {
          errorMessages.push('Password cannot be entirely numeric.');
        } else if (error.code === 'password_too_short') {
          errorMessages.push('Password must be at least 8 characters long.');
        } else if (error.code === 'password_too_similar') {
          errorMessages.push('Password is too similar to your personal information.');
        } else if (error.string) {
          errorMessages.push(error.string);
        }
      });
    }

    if (errorMessage.confirm_password && Array.isArray(errorMessage.confirm_password)) {
      errorMessage.confirm_password.forEach((error: { string: string }) => {
        errorMessages.push(error.string || 'Password confirmation does not match.');
      });
    }

    return errorMessages.length > 0
      ? errorMessages.join(' ')
      : 'Password validation failed. Please check your input.';
  }
}

export interface ResetModel {
  uid: string | null;
  token: string | null;
  new_password: string;
  confirm_password: string;
}
